import { batch, computed, signal } from "@preact/signals-core";

// A snapshot key holding an array becomes a collection keyed by id; any other
// value (the scene) is a single record. Ops address both the same way:
//   { op: "upsert" | "update" | "remove", collection, id, attrs }
// "update" merges only into a record that exists, so a pending move never
// resurrects a token someone else removed.
/**
 * @template {object} [Local={}]
 * @param {object} [snapshot]
 * @param {{ local?: Local }} [options] UI state the server never touches.
 */
export function createLiveState(snapshot = {}, { local = /** @type {Local} */ ({}) } = {}) {
	const singles = new Set();
	const confirmed = signal({});
	const version = signal(0);
	const pending = signal([]);

	const view = computed(() => pending.value.reduce((data, entry) => applyOps(data, entry.ops, singles), confirmed.value));

	function replaceSnapshot(next) {
		batch(() => {
			singles.clear();
			confirmed.value = normalize(next, singles);
			version.value = next.version ?? 0;
		});
	}

	function applyDiff({ version: nextVersion = null, ops, mutationId = null }) {
		batch(() => {
			confirmed.value = applyOps(confirmed.peek(), ops, singles);
			if (nextVersion != null) version.value = nextVersion;
			if (mutationId) removePending(mutationId);
		});
	}

	function addPending({ mutationId, ops = [] }) {
		pending.value = [...pending.peek(), { mutationId, ops, acked: false }];
	}

	function acknowledge(mutationId) {
		pending.value = pending.peek().map((entry) => (entry.mutationId === mutationId ? { ...entry, acked: true } : entry));
	}

	function removePending(mutationId) {
		const entries = pending.peek();
		const remaining = entries.filter((entry) => entry.mutationId !== mutationId);
		if (remaining.length === entries.length) return false;
		pending.value = remaining;
		return true;
	}

	replaceSnapshot(snapshot);

	return {
		confirmed,
		pending,
		view,
		local,
		version,
		applyDiff,
		replaceSnapshot,
		addPending,
		acknowledge,
		resolvePending: removePending,
		dropPending: removePending,
	};
}

export function createSceneState(snapshot = {}) {
	return createLiveState(snapshot, {
		local: {
			viewport: signal({ x: 0, y: 0, zoom: 1 }),
			selection: signal([]),
			// The master's "Selecionar várias": while on, each tap on a piece adds it to the selection or takes it out.
			multi: signal(false),
			// The master's tick mode for Iniciar combate: the monsters that may join and the ones ticked, or null.
			ticks: signal(/** @type {{ eligible: string[], ticked: string[] } | null} */ (null)),
			// The master's combat marks by token id, set by the Combate panel's island from the encounter's live client; the scene's
			// own state and diffs never carry them.
			combat: signal(/** @type {Record<string, { monster: boolean, pv: number, max: number, turn: boolean, defeated: boolean }>} */ ({})),
			// The piece or drawing whose resize and rotate handles show, the master's only.
			handles: signal(/** @type {string | null} */ (null)),
			// The master's selected note and whether the note tool is armed, for the Notas panel and the marks.
			note: signal(/** @type {string | null} */ (null)),
			noteTool: signal(false),
			// Whether this device hides the note marks; the panel's eye sets it.
			notesHidden: signal(false),
			// The drawing whose menu is open, the master's only.
			drawingMenu: signal(/** @type {string | null} */ (null)),
			drafts: signal(
				/** @type {{ grid?: object, size?: { width_px: number, height_px: number }, resize?: import("../maps/resize").Resize, step?: { id: string, x?: number, y?: number, rotation?: number }, group?: Record<string, { x: number, y: number }>, transform?: { id: string, size?: number, rotation?: number, span?: number | null, x?: number, y?: number }, terrain?: import("../maps/cuts").Terrain, edges?: import("../maps/edges").Edges, edgePick?: import("../maps/edges").Mark[], fog?: import("../maps/fog").Fog, fogPick?: import("../maps/fog_pick_drawing").FogPick, drawing?: import("../maps/drawing_layer").Draft, move?: { id: string, dx: number, dy: number }, reshape?: { id: string, points: number[] } }} */ ({}),
			),
		},
	});
}

function normalize(snapshot, singles) {
	const data = {};
	for (const [key, value] of Object.entries(snapshot ?? {})) {
		if (key === "version") continue;
		if (Array.isArray(value)) {
			data[key] = Object.fromEntries(value.map((record) => [record.id, record]));
		} else {
			singles.add(key);
			data[key] = value ?? null;
		}
	}
	return data;
}

function applyOps(data, ops, singles) {
	if (!ops?.length) return data;
	const next = { ...data };
	const copied = new Set();
	for (const { op, collection, id, attrs } of ops) {
		if (singles.has(collection)) {
			const current = next[collection];
			if (op === "remove") next[collection] = null;
			else if (op !== "update" || current) next[collection] = { ...current, ...attrs, id: id ?? current?.id };
			continue;
		}
		if (!copied.has(collection)) {
			next[collection] = { ...next[collection] };
			copied.add(collection);
		}
		const records = next[collection];
		if (op === "remove") delete records[id];
		else if (op !== "update" || records[id]) records[id] = { ...records[id], ...attrs, id };
	}
	return next;
}
